import { createFileRoute } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

export const Route = createFileRoute("/dev/seat-stress")({
  head: () => ({ meta: [{ title: "Seat Editor Stress Test — ZYNTRA" }] }),
  component: SeatStressPage,
});

// ============================================================
// Mock seat model — mirrors SeatEditor's free-mode canvas exactly
// ============================================================
type Seat = {
  id: string;
  seat_label: string;
  seat_type: "standard" | "vip" | "accessibility";
  status: "available" | "blocked" | "aisle";
  x_position: number;
  y_position: number;
};

function makeSeats(rowCount: number, perRow: number): Seat[] {
  const out: Seat[] = [];
  const padX = 0.04;
  const padY = 0.06;
  const gridW = 1 - padX * 2;
  const gridH = 1 - padY * 2;
  for (let r = 0; r < rowCount; r++) {
    for (let c = 0; c < perRow; c++) {
      out.push({
        id: `s-${r}-${c}`,
        seat_label: `${String.fromCharCode(65 + (r % 26))}${c + 1}`,
        seat_type: "standard",
        status: "available",
        x_position: padX + (c / Math.max(1, perRow - 1)) * gridW,
        y_position: padY + (r / Math.max(1, rowCount - 1)) * gridH,
      });
    }
  }
  return out;
}

function statusColor(s: Seat) {
  if (s.status === "blocked") return "bg-destructive/30 border-destructive text-destructive-foreground";
  if (s.status === "aisle") return "bg-transparent border-dashed border-muted-foreground/40 text-muted-foreground";
  if (s.seat_type === "vip") return "bg-amber-400/30 border-amber-400 text-amber-100";
  if (s.seat_type === "accessibility") return "bg-blue-400/30 border-blue-400 text-blue-100";
  return "border-border text-foreground hover:brightness-125";
}

// ============================================================
// SeatNode — same memoization pattern as SeatEditor
// ============================================================
type SeatNodeProps = {
  seat: Seat;
  isSelected: boolean;
  registerRef: (id: string, el: HTMLButtonElement | null) => void;
  onPointerDown: (e: React.PointerEvent, s: Seat) => void;
};

const SeatNode = memo(function SeatNode({ seat, isSelected, registerRef, onPointerDown }: SeatNodeProps) {
  const x = seat.x_position;
  const y = seat.y_position;
  return (
    <button
      ref={(el) => registerRef(seat.id, el)}
      data-seat-id={seat.id}
      onPointerDown={(e) => onPointerDown(e, seat)}
      className={`absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-md border-2 text-[8px] font-bold transition-shadow active:cursor-grabbing ${statusColor(seat)} ${isSelected ? "ring-2 ring-primary ring-offset-1 ring-offset-card" : ""}`}
      style={{ left: `${x * 100}%`, top: `${y * 100}%` }}
    >
      {seat.seat_label}
    </button>
  );
});

// ============================================================
// Page
// ============================================================
type Mode = "optimized" | "naive";
type DragKind = "single" | "group";

type FpsResult = {
  mode: Mode;
  kind: DragKind;
  seatCount: number;
  groupSize: number;
  durationMs: number;
  frames: number;
  avgFps: number;
  minFps: number;
  p95FrameMs: number;
  maxFrameMs: number;
};

function SeatStressPage() {
  const [rowCount, setRowCount] = useState(40);
  const [perRow, setPerRow] = useState(25);
  const [seats, setSeats] = useState<Seat[]>(() => makeSeats(40, 25));
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<Mode>("optimized");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<FpsResult[]>([]);
  const [liveFps, setLiveFps] = useState<number | null>(null);

  const canvasRef = useRef<HTMLDivElement | null>(null);
  const seatRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const seatsRef = useRef<Seat[]>(seats);
  seatsRef.current = seats;
  const selectedRef = useRef<Set<string>>(selectedIds);
  selectedRef.current = selectedIds;

  // Drag state — same shape as SeatEditor
  const dragRef = useRef<
    | { mode: "single"; seatId: string; offX: number; offY: number; startX: number; startY: number }
    | { mode: "group"; ids: string[]; startPx: number; startPy: number; baseline: Map<string, { x: number; y: number }> }
    | null
  >(null);
  const liveOffsetsRef = useRef<Map<string, { dx: number; dy: number }>>(new Map());
  const rafIdRef = useRef<number | null>(null);
  const pendingMoveRef = useRef<{ clientX: number; clientY: number } | null>(null);

  const registerSeatRef = useCallback((id: string, el: HTMLButtonElement | null) => {
    if (el) seatRefs.current.set(id, el);
    else seatRefs.current.delete(id);
  }, []);

  function regenerate() {
    setSeats(makeSeats(rowCount, perRow));
    setSelectedIds(new Set());
    setResults([]);
  }

  function moveSeats(deltas: Array<{ id: string; x: number; y: number }>) {
    const map = new Map(deltas.map((d) => [d.id, { x: Math.max(0, Math.min(1, d.x)), y: Math.max(0, Math.min(1, d.y)) }]));
    setSeats((prev) => prev.map((s) => (map.has(s.id) ? { ...s, x_position: map.get(s.id)!.x, y_position: map.get(s.id)!.y } : s)));
  }

  // ==========================================================
  // OPTIMIZED path — rAF coalescing + ref-based DOM transform
  // ==========================================================
  function applyLiveTransform(id: string, dx: number, dy: number, rect: DOMRect) {
    const el = seatRefs.current.get(id);
    if (!el) return;
    const pxX = dx * rect.width;
    const pxY = dy * rect.height;
    el.style.transform = `translate(calc(-50% + ${pxX}px), calc(-50% + ${pxY}px))`;
  }
  function clearLiveTransforms() {
    for (const id of liveOffsetsRef.current.keys()) {
      const el = seatRefs.current.get(id);
      if (el) el.style.transform = "";
    }
    liveOffsetsRef.current.clear();
  }
  function scheduleFrame() {
    if (rafIdRef.current != null) return;
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      const d = dragRef.current;
      const ev = pendingMoveRef.current;
      if (!d || !ev || !canvasRef.current) return;
      const rect = canvasRef.current.getBoundingClientRect();
      const px = (ev.clientX - rect.left) / rect.width;
      const py = (ev.clientY - rect.top) / rect.height;
      if (d.mode === "single") {
        const newX = Math.max(0, Math.min(1, px - d.offX));
        const newY = Math.max(0, Math.min(1, py - d.offY));
        const dx = newX - d.startX;
        const dy = newY - d.startY;
        liveOffsetsRef.current.set(d.seatId, { dx, dy });
        applyLiveTransform(d.seatId, dx, dy, rect);
      } else {
        const dxN = px - d.startPx;
        const dyN = py - d.startPy;
        for (const id of d.ids) {
          const base = d.baseline.get(id);
          if (!base) continue;
          const fx = Math.max(0, Math.min(1, base.x + dxN));
          const fy = Math.max(0, Math.min(1, base.y + dyN));
          const dx = fx - base.x;
          const dy = fy - base.y;
          liveOffsetsRef.current.set(id, { dx, dy });
          applyLiveTransform(id, dx, dy, rect);
        }
      }
    });
  }

  // ==========================================================
  // NAIVE path — setState on every move (worst case baseline)
  // ==========================================================
  function naiveMove(clientX: number, clientY: number) {
    const d = dragRef.current;
    if (!d || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (clientX - rect.left) / rect.width;
    const py = (clientY - rect.top) / rect.height;
    if (d.mode === "single") {
      moveSeats([{ id: d.seatId, x: px - d.offX, y: py - d.offY }]);
    } else {
      const dxN = px - d.startPx;
      const dyN = py - d.startPy;
      const deltas: Array<{ id: string; x: number; y: number }> = [];
      for (const id of d.ids) {
        const base = d.baseline.get(id);
        if (!base) continue;
        deltas.push({ id, x: base.x + dxN, y: base.y + dyN });
      }
      moveSeats(deltas);
    }
  }

  // ==========================================================
  // Pointer handlers
  // ==========================================================
  const onSeatPointerDown = useCallback((e: React.PointerEvent, s: Seat) => {
    if (!canvasRef.current) return;
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const sel = selectedRef.current;
    if (sel.size > 1 && sel.has(s.id)) {
      const baseline = new Map<string, { x: number; y: number }>();
      const seatMap = new Map(seatsRef.current.map((seat) => [seat.id, seat]));
      const ids: string[] = [];
      for (const id of sel) {
        const seat = seatMap.get(id);
        if (!seat) continue;
        baseline.set(id, { x: seat.x_position, y: seat.y_position });
        ids.push(id);
      }
      dragRef.current = { mode: "group", ids, startPx: px, startPy: py, baseline };
    } else {
      setSelectedIds(new Set([s.id]));
      dragRef.current = { mode: "single", seatId: s.id, offX: px - s.x_position, offY: py - s.y_position, startX: s.x_position, startY: s.y_position };
    }
  }, []);

  function onCanvasPointerMove(e: React.PointerEvent) {
    if (!dragRef.current) return;
    if (mode === "optimized") {
      pendingMoveRef.current = { clientX: e.clientX, clientY: e.clientY };
      scheduleFrame();
    } else {
      naiveMove(e.clientX, e.clientY);
    }
  }
  function onCanvasPointerUp() {
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    pendingMoveRef.current = null;
    const d = dragRef.current;
    if (d && mode === "optimized" && liveOffsetsRef.current.size > 0) {
      const seatMap = new Map(seatsRef.current.map((s) => [s.id, s]));
      const deltas: Array<{ id: string; x: number; y: number }> = [];
      for (const [id, off] of liveOffsetsRef.current) {
        const s = seatMap.get(id);
        if (!s) continue;
        deltas.push({ id, x: s.x_position + off.dx, y: s.y_position + off.dy });
      }
      clearLiveTransforms();
      if (deltas.length > 0) moveSeats(deltas);
    }
    dragRef.current = null;
  }

  useEffect(() => () => {
    if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
  }, []);

  // ==========================================================
  // Auto-drag benchmark
  // ==========================================================
  async function runBenchmark(kind: DragKind, durationMs = 3000, groupSize = 50) {
    if (!canvasRef.current) return;
    setRunning(true);
    setLiveFps(null);

    // Pick the center-most seat as anchor
    const target = seatsRef.current.reduce((best, s) => {
      const d = Math.hypot(s.x_position - 0.5, s.y_position - 0.5);
      return d < best.d ? { d, s } : best;
    }, { d: Infinity, s: seatsRef.current[0] }).s;

    // Build group selection if needed (closest N seats to target)
    if (kind === "group") {
      const sorted = [...seatsRef.current].sort((a, b) =>
        Math.hypot(a.x_position - target.x_position, a.y_position - target.y_position) -
        Math.hypot(b.x_position - target.x_position, b.y_position - target.y_position)
      );
      const ids = new Set(sorted.slice(0, groupSize).map((s) => s.id));
      setSelectedIds(ids);
      // Wait one frame for selection state to apply
      await new Promise((r) => requestAnimationFrame(() => r(null)));
      await new Promise((r) => requestAnimationFrame(() => r(null)));
    } else {
      setSelectedIds(new Set([target.id]));
      await new Promise((r) => requestAnimationFrame(() => r(null)));
    }

    const rect = canvasRef.current.getBoundingClientRect();
    const cx = rect.left + target.x_position * rect.width;
    const cy = rect.top + target.y_position * rect.height;

    // Synthesize pointerdown on target seat
    const seatEl = seatRefs.current.get(target.id);
    if (!seatEl) {
      setRunning(false);
      return;
    }
    const downEvent = new PointerEvent("pointerdown", {
      pointerId: 1, bubbles: true, cancelable: true, clientX: cx, clientY: cy, button: 0, pointerType: "mouse",
    });
    seatEl.dispatchEvent(downEvent);

    // Drive a circular motion for `durationMs`
    const radius = Math.min(rect.width, rect.height) * 0.15;
    const start = performance.now();
    let lastFrame = start;
    const frameTimes: number[] = [];

    await new Promise<void>((resolve) => {
      function tick(now: number) {
        const elapsed = now - start;
        if (elapsed >= durationMs) {
          resolve();
          return;
        }
        frameTimes.push(now - lastFrame);
        lastFrame = now;
        // Move pointer in circle
        const t = elapsed / 300; // ~3.3 rad/s
        const x = cx + Math.cos(t) * radius;
        const y = cy + Math.sin(t) * radius;
        const moveEvent = new PointerEvent("pointermove", {
          pointerId: 1, bubbles: true, cancelable: true, clientX: x, clientY: y, button: 0, pointerType: "mouse",
        });
        canvasRef.current?.dispatchEvent(moveEvent);
        // Live FPS readout (every ~10 frames)
        if (frameTimes.length % 10 === 0) {
          const recent = frameTimes.slice(-30);
          const avg = recent.reduce((a, b) => a + b, 0) / recent.length;
          setLiveFps(Math.round(1000 / avg));
        }
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    });

    // pointerup
    const upEvent = new PointerEvent("pointerup", {
      pointerId: 1, bubbles: true, cancelable: true, clientX: cx, clientY: cy, button: 0, pointerType: "mouse",
    });
    canvasRef.current.dispatchEvent(upEvent);

    // Drop first frame (warmup)
    const ft = frameTimes.slice(1);
    const totalMs = ft.reduce((a, b) => a + b, 0);
    const avgMs = totalMs / ft.length;
    const sorted = [...ft].sort((a, b) => a - b);
    const p95 = sorted[Math.floor(sorted.length * 0.95)];
    const maxMs = sorted[sorted.length - 1];
    const minFps = 1000 / maxMs;
    const result: FpsResult = {
      mode,
      kind,
      seatCount: seatsRef.current.length,
      groupSize: kind === "group" ? groupSize : 1,
      durationMs: Math.round(totalMs),
      frames: ft.length,
      avgFps: Math.round((1000 / avgMs) * 10) / 10,
      minFps: Math.round(minFps * 10) / 10,
      p95FrameMs: Math.round(p95 * 100) / 100,
      maxFrameMs: Math.round(maxMs * 100) / 100,
    };
    setResults((prev) => [...prev, result]);
    setLiveFps(null);
    setRunning(false);
  }

  async function runFullSuite() {
    // Run optimized then naive, single + group, with regen between modes
    setResults([]);
    const orig = mode;
    for (const m of ["optimized", "naive"] as Mode[]) {
      setMode(m);
      await new Promise((r) => setTimeout(r, 100));
      await runBenchmark("single", 3000);
      await new Promise((r) => setTimeout(r, 200));
      await runBenchmark("group", 3000, 50);
      await new Promise((r) => setTimeout(r, 200));
    }
    setMode(orig);
  }

  const totalSeats = seats.length;

  return (
    <div className="min-h-screen bg-background p-6 text-foreground">
      <div className="mx-auto max-w-6xl">
        <h1 className="mb-1 text-2xl font-black">SeatEditor — Stress Test Harness</h1>
        <p className="mb-4 text-sm text-muted-foreground">
          Mide FPS durante drag programático sobre {totalSeats} asientos. Compara modo <code className="text-primary">optimized</code> (rAF + ref-based) vs <code className="text-primary">naive</code> (setState por pointermove).
        </p>

        {/* Controls */}
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-4">
          <div>
            <label className="block text-[10px] font-semibold uppercase text-muted-foreground">Filas</label>
            <input
              type="number" min={1} max={80} value={rowCount}
              onChange={(e) => setRowCount(Math.max(1, Math.min(80, Number(e.target.value) || 1)))}
              className="w-20 rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase text-muted-foreground">Asientos x fila</label>
            <input
              type="number" min={1} max={80} value={perRow}
              onChange={(e) => setPerRow(Math.max(1, Math.min(80, Number(e.target.value) || 1)))}
              className="w-20 rounded-lg border border-border bg-background px-2 py-1.5 text-sm"
            />
          </div>
          <button onClick={regenerate} disabled={running} className="rounded-lg border border-border bg-secondary px-3 py-2 text-xs font-bold text-secondary-foreground disabled:opacity-50">
            Regenerar ({rowCount * perRow})
          </button>
          <div className="ml-auto inline-flex items-center gap-1 rounded-lg border border-border bg-background p-0.5">
            <button onClick={() => setMode("optimized")} disabled={running} className={`rounded-md px-3 py-1 text-[11px] font-semibold ${mode === "optimized" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              Optimized
            </button>
            <button onClick={() => setMode("naive")} disabled={running} className={`rounded-md px-3 py-1 text-[11px] font-semibold ${mode === "naive" ? "bg-destructive text-destructive-foreground" : "text-muted-foreground"}`}>
              Naive (baseline)
            </button>
          </div>
        </div>

        {/* Run buttons */}
        <div className="mb-4 flex flex-wrap gap-2">
          <button onClick={() => runBenchmark("single", 3000)} disabled={running}
            className="rounded-lg gradient-primary px-4 py-2 text-xs font-bold text-primary-foreground glow-primary disabled:opacity-50">
            ▶ Drag individual (3s)
          </button>
          <button onClick={() => runBenchmark("group", 3000, 50)} disabled={running}
            className="rounded-lg gradient-primary px-4 py-2 text-xs font-bold text-primary-foreground glow-primary disabled:opacity-50">
            ▶ Drag grupo de 50 (3s)
          </button>
          <button onClick={runFullSuite} disabled={running}
            className="rounded-lg border-2 border-primary bg-background px-4 py-2 text-xs font-bold text-primary disabled:opacity-50">
            ▶▶ Suite completa (4 tests)
          </button>
          {liveFps != null && (
            <span className="ml-auto rounded-lg bg-primary/20 px-3 py-2 text-xs font-bold text-primary">
              Live: {liveFps} fps
            </span>
          )}
        </div>

        {/* Canvas */}
        <div className="space-y-2">
          <div
            ref={canvasRef}
            onPointerMove={onCanvasPointerMove}
            onPointerUp={onCanvasPointerUp}
            onPointerLeave={onCanvasPointerUp}
            className="relative mx-auto aspect-[16/10] w-full max-w-3xl touch-none select-none rounded-xl border border-border bg-surface/40"
            style={{
              backgroundImage:
                "linear-gradient(to right, color-mix(in oklab, var(--border) 40%, transparent) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in oklab, var(--border) 40%, transparent) 1px, transparent 1px)",
              backgroundSize: "5% 8.33%",
            }}
          >
            {seats.map((s) => (
              <SeatNode
                key={s.id}
                seat={s}
                isSelected={selectedIds.has(s.id)}
                registerRef={registerSeatRef}
                onPointerDown={onSeatPointerDown}
              />
            ))}
          </div>
        </div>

        {/* Results */}
        {results.length > 0 && (
          <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-xs">
              <thead className="bg-secondary text-secondary-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Mode</th>
                  <th className="px-3 py-2 text-left">Kind</th>
                  <th className="px-3 py-2 text-right">Seats</th>
                  <th className="px-3 py-2 text-right">Group</th>
                  <th className="px-3 py-2 text-right">Frames</th>
                  <th className="px-3 py-2 text-right">Avg FPS</th>
                  <th className="px-3 py-2 text-right">Min FPS</th>
                  <th className="px-3 py-2 text-right">p95 frame ms</th>
                  <th className="px-3 py-2 text-right">Max frame ms</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className={`px-3 py-2 font-bold ${r.mode === "optimized" ? "text-primary" : "text-destructive"}`}>{r.mode}</td>
                    <td className="px-3 py-2">{r.kind}</td>
                    <td className="px-3 py-2 text-right">{r.seatCount}</td>
                    <td className="px-3 py-2 text-right">{r.groupSize}</td>
                    <td className="px-3 py-2 text-right">{r.frames}</td>
                    <td className="px-3 py-2 text-right font-bold">{r.avgFps}</td>
                    <td className="px-3 py-2 text-right">{r.minFps}</td>
                    <td className="px-3 py-2 text-right">{r.p95FrameMs}</td>
                    <td className="px-3 py-2 text-right">{r.maxFrameMs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
